/**
 * Audit log.
 *
 * Append-only. Every mutation in the system produces at least one row here:
 *   - Field-level updates: one row per changed field with old/new JSON
 *   - Lifecycle events (CREATE, DELETE, SUBMIT_PARTS, etc.): single rows
 *     with descriptive metadata in `metadataJson`
 *
 * The DB role `procurement_app` has INSERT-only privilege on this table.
 * Even Admin cannot edit or delete entries from the application.
 */

import { Type, type Static } from '@sinclair/typebox';
import { AuditActions, AuditEntityTypes } from '../enums.js';
import { IsoDateTimeSchema, PaginatedResponseSchema, UuidSchema } from './common.js';

// ---------------------------------------------------------------------------
// Audit entry
// ---------------------------------------------------------------------------

export const AuditEntrySchema = Type.Object({
  id: UuidSchema,
  occurredAt: IsoDateTimeSchema,
  userId: UuidSchema,
  /** Joined for display convenience; not stored on this table. */
  userDisplayName: Type.String(),

  entityType: Type.Union(AuditEntityTypes.map((t) => Type.Literal(t))),
  entityId: UuidSchema,
  /** Scope: which project this audit entry belongs to (null for master records). */
  projectId: Type.Union([UuidSchema, Type.Null()]),

  action: Type.Union(AuditActions.map((a) => Type.Literal(a))),

  /** Set for UPDATE actions; null for lifecycle events. */
  fieldName: Type.Union([Type.String(), Type.Null()]),
  /** Old and new values, JSON-encoded. Null when the action isn't a field update. */
  oldValueJson: Type.Union([Type.String(), Type.Null()]),
  newValueJson: Type.Union([Type.String(), Type.Null()]),

  /** Free-form context (rejection comment, cancellation reason, etc.). */
  metadataJson: Type.Union([Type.String(), Type.Null()]),
});
export type AuditEntry = Static<typeof AuditEntrySchema>;

// ---------------------------------------------------------------------------
// Query and list
// ---------------------------------------------------------------------------

export const AuditQuerySchema = Type.Object({
  projectId: Type.Optional(UuidSchema),
  userId: Type.Optional(UuidSchema),
  entityType: Type.Optional(Type.Union(AuditEntityTypes.map((t) => Type.Literal(t)))),
  entityId: Type.Optional(UuidSchema),
  action: Type.Optional(Type.Union(AuditActions.map((a) => Type.Literal(a)))),
  fromDate: Type.Optional(IsoDateTimeSchema),
  toDate: Type.Optional(IsoDateTimeSchema),
  page: Type.Optional(Type.Integer({ minimum: 1 })),
  pageSize: Type.Optional(Type.Integer({ minimum: 1, maximum: 200 })),
  // Patch 5 — sortable columns. Whitelist of sortable keys; default is the
  // existing reverse-chronological order. `occurredAt` matches the default
  // so the URL stays clean when nothing is explicitly chosen.
  sortBy: Type.Optional(
    Type.Union([
      Type.Literal('occurredAt'),
      Type.Literal('action'),
      Type.Literal('entityType'),
      Type.Literal('userDisplayName'),
    ]),
  ),
  sortDir: Type.Optional(Type.Union([Type.Literal('asc'), Type.Literal('desc')])),
});
export type AuditQuery = Static<typeof AuditQuerySchema>;

export const AuditListResponseSchema = PaginatedResponseSchema(AuditEntrySchema);
export type AuditListResponse = Static<typeof AuditListResponseSchema>;
